import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FaFileAlt, FaFilePdf, FaFolderOpen } from 'react-icons/fa'
import { AuthAvatar } from '../ui'
import { getCatalogLabel } from '../../constants/ticket'
import { toSafeHtml } from '../../utils/richTextHtml'
import { formatAssigneeNames, getTicketAssignees } from '../../utils/ticketAssignees'
import { formatDate, formatDateTime, getAttachmentInfo } from '../../utils/projectHelpers'
import ProjectStatusBadge from './ProjectStatusBadge'
import LangSwitch from '../ui/LangSwitch'
import AbsatBotBadge, { isAbsatBot } from '../ui/AbsatBotBadge'
import { hasBothLanguages, localizedText } from '../../utils/localized'

const Field = ({ label, children }) => (
    <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflowWrap: 'anywhere' }}>{children || '-'}</div>
    </div>
);

// كارت بيانات المشروع — يُستخدم في صفحة المشروع وأعلى صفحة التذكرة المرتبطة به.
// `attachmentsSlot` بيستبدل قائمة المرفقات (صفحة المشروع بتبعت مكوّن الرفع / الحذف).
const ProjectInfoCard = ({ project, supportSettings, lang = 'ar', admins = [], showLink = false, showDescription = true, attachmentsSlot = null }) => {
    const params = useParams();
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const [viewLang, setViewLang] = useState(lang === 'en' ? 'en' : 'ar');
    if (!project) return null;
    const isAr = lang === 'ar';
    const assignees = getTicketAssignees(project).map((entry) => {
        const admin = admins.find((item) => String(item?._id) === entry.userId);
        return {
            userId: entry.userId,
            name: entry.name || admin?.name || admin?.username || entry.email || entry.userId,
            avatar: admin?.userPic || admin?.avatar || admin?.image || admin?.profileImage || admin?.photo || ''
        };
    });
    const creatorAdmin = project.createdBy ? admins.find((item) => String(item?._id) === String(project.createdBy)) : null;
    const creatorName = project.createdByName || creatorAdmin?.name || creatorAdmin?.username || creatorAdmin?.email || '';
    const creator = creatorName
        ? { name: creatorName, avatar: creatorAdmin?.userPic || creatorAdmin?.avatar || creatorAdmin?.image || '' }
        : null;
    const attachments = Array.isArray(project.attachments) ? project.attachments.filter(Boolean) : [];
    const section = project.sectionId ? getCatalogLabel(supportSettings?.sections, project.sectionId, lang) : '';
    const product = project.ProductId ? getCatalogLabel(supportSettings?.products, project.ProductId, lang) : '';

    return (
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #c7d2fe', boxShadow: '0 2px 12px rgba(79,70,229,0.06)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 18px', background: '#eef2ff', borderBottom: '1px solid #c7d2fe' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span style={{ width: 34, height: 34, borderRadius: 10, background: '#4f46e5', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FaFolderOpen size={16} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11, fontWeight: 800, color: '#4f46e5' }}>
                            <span>{isAr ? 'المشروع' : 'Project'} #{project.projectNumber ?? '-'}</span>
                            <ProjectStatusBadge status={project.status} lang={lang} size="sm" />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <div dir="auto" style={{ fontSize: 16, fontWeight: 900, color: '#0f172a', overflowWrap: 'anywhere' }}>{localizedText(project, 'title', viewLang)}</div>
                            {hasBothLanguages(project, ['title', 'description']) && (
                                <LangSwitch value={viewLang} onChange={setViewLang} isAr={lang !== 'en'} filled={{ ar: true, en: true }} />
                            )}
                        </div>
                    </div>
                </div>
                {showLink && (
                    <Link
                        to={`/${params.tenant}/projects/show/${project._id}`}
                        style={{ fontSize: 12, fontWeight: 800, color: '#4f46e5', padding: '6px 12px', borderRadius: 999, border: '1px solid #c7d2fe', background: '#fff', textDecoration: 'none' }}
                    >
                        {isAr ? 'فتح المشروع' : 'Open project'}
                    </Link>
                )}
            </div>

            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                    <Field label={isAr ? 'العميل' : 'Customer'}>{project.tenantName || project.customerName}</Field>
                    <Field label={isAr ? 'معينة لـ' : 'Assigned To'}>
                        {assignees.length ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                {assignees.map((entry) => (
                                    <AuthAvatar key={entry.userId} src={entry.avatar} alt={entry.name} size={20} />
                                ))}
                                <span>{formatAssigneeNames(assignees.map((entry) => entry.name), lang)}</span>
                            </span>
                        ) : null}
                    </Field>
                    <Field label={isAr ? 'القسم / المنتج' : 'Section / Product'}>
                        {[section, product].filter(Boolean).join(' / ')}
                    </Field>
                    <Field label={isAr ? 'رقم العقد' : 'Contract number'}>{project.contractNumber ? <span dir="ltr">{project.contractNumber}</span> : null}</Field>
                    <Field label={isAr ? 'تاريخ البداية' : 'Start date'}>{formatDate(project.startAt)}</Field>
                    <Field label={isAr ? 'تاريخ النهاية المتوقع' : 'Expected end date'}>{formatDate(project.expectedAt)}</Field>
                    <Field label={isAr ? 'تاريخ إنشاء المشروع' : 'Created at'}>{formatDateTime(project.createdAt)}</Field>
                    <Field label={isAr ? 'منشئ المشروع' : 'Created by'}>
                        {isAbsatBot(project) ? (
                            <AbsatBotBadge scheduled lang={lang} />
                        ) : creator ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <AuthAvatar src={creator.avatar} alt={creator.name} size={20} />
                                <span>{creator.name}</span>
                            </span>
                        ) : null}
                    </Field>
                </div>

                {showDescription && localizedText(project, 'description', viewLang) && (
                    <div style={{ paddingTop: 14, borderTop: '1px dashed #e2e8f0' }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 6 }}>{isAr ? 'الوصف' : 'Description'}</div>
                        {/* الوصف الطويل بيتعرض في مساحة ثابتة بـ scroll داخلي، ويتفتح بالكامل عند الطلب. */}
                        <div
                            className="mw-rich-content table-scroll"
                            dir="auto"
                            style={{ fontSize: 14, lineHeight: 1.85, color: '#334155', unicodeBidi: 'plaintext', textAlign: 'start', overflowWrap: 'anywhere', maxHeight: descriptionExpanded ? 'none' : 220, overflowY: descriptionExpanded ? 'visible' : 'auto', paddingInlineEnd: 6, background: '#fcfcfd', border: '1px solid #f1f5f9', borderRadius: 10, padding: '8px 12px' }}
                            dangerouslySetInnerHTML={{ __html: toSafeHtml(localizedText(project, 'description', viewLang)) }}
                        />
                        <button
                            type="button"
                            onClick={() => setDescriptionExpanded((v) => !v)}
                            style={{ marginTop: 6, padding: '3px 10px', borderRadius: 999, border: '1px solid #e0e7ff', background: '#eef2ff', color: '#4f46e5', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}
                        >
                            {descriptionExpanded ? (isAr ? 'عرض أقل' : 'Show less') : (isAr ? 'عرض الوصف بالكامل' : 'Show full description')}
                        </button>
                    </div>
                )}

                {attachmentsSlot ? (
                    <div style={{ paddingTop: 14, borderTop: '1px dashed #e2e8f0' }}>{attachmentsSlot}</div>
                ) : attachments.length > 0 && (
                    <div style={{ paddingTop: 14, borderTop: '1px dashed #e2e8f0' }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 10 }}>{isAr ? 'مرفقات المشروع' : 'Project attachments'}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                            {attachments.map((url) => {
                                const info = getAttachmentInfo(url, project.attachmentNames);
                                return (
                                    <a
                                        key={url}
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, background: '#f8fafc', border: '1.5px solid #e2e8f0', textDecoration: 'none', minWidth: 0 }}
                                    >
                                        {info.isImage ? (
                                            <img src={url} alt={info.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 8, flexShrink: 0, border: '1px solid #e2e8f0' }} />
                                        ) : (
                                            <span style={{ width: 40, height: 40, borderRadius: 8, flexShrink: 0, background: info.isPdf ? '#fff1f2' : '#f0f9ff', color: info.isPdf ? '#ef4444' : '#0ea5e9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {info.isPdf ? <FaFilePdf size={18} /> : <FaFileAlt size={18} />}
                                            </span>
                                        )}
                                        <span style={{ minWidth: 0 }}>
                                            <span dir="auto" style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{info.name}</span>
                                            <span style={{ display: 'block', fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{info.ext}</span>
                                        </span>
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProjectInfoCard;
